/**
 * WelcomeSecret — the Pilot Simulator front door.
 *
 * The dashboard greeting is the trigger: tap the name and it answers, keep
 * tapping and the answer escalates (the shine sweeps faster, drone motes lift
 * off, the room dims) until the seventh tap fades everything to black, brings
 * the stars out, shows the welcome note, and drops the player into the game.
 * Stop tapping at any point and it settles back like nothing happened.
 *
 * The reveal overlay is deliberately plain DOM appended to document.body, NOT
 * a React portal: history.push unmounts this component (with the whole
 * dashboard) mid-sequence, and the overlay must outlive it to cover the route
 * swap. Every path out of the sequence removes the node on a timer.
 *
 * Firefleet-only: on Swarm the name renders inert with no handlers at all.
 */
import { useCallback, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routeAllowedOnInstance } from '../../navigation/routeInstances';
import { useTapSecret } from './useTapSecret';
import './WelcomeSecret.css';

const TAPS = 7;
const GAP_MS = 1200;
const MOTES_PER_STAGE = [0, 1, 2, 3, 5, 8, 12];
const MOTE_COLORS = ['#ffcc33', '#7fd4ff', '#ff7ad9', '#9dff8b'];
const NOTE = 'Welcome to Pilot Simulator. You found it.';
const RULE = "Don't tell anyone. The hunt is half the fun.";

function runReveal(navigate: () => void) {
  // one reveal at a time, guarded at the DOM: the component's own flag dies with
  // it at navigation, but the overlay outlives it by design
  if (document.querySelector('.ws-reveal')) return;
  // the seven taps usually leave a text selection on the page; drop it so the
  // splash comes up clean
  const sel = window.getSelection();
  if (sel) sel.removeAllRanges();
  const ov = document.createElement('div');
  ov.className = 'ws-reveal';
  for (let i = 0; i < 90; i++) {
    const s = document.createElement('span');
    s.className = 'ws-star';
    const size = Math.random() < 0.15 ? 2.5 : 1.5;
    s.style.width = s.style.height = size + 'px';
    s.style.left = Math.random() * 100 + '%';
    s.style.top = Math.random() * 100 + '%';
    s.style.setProperty('--d', (Math.random() * 2.4).toFixed(2) + 's');
    s.style.setProperty('--o', (0.3 + Math.random() * 0.7).toFixed(2));
    ov.appendChild(s);
  }
  const note = document.createElement('div');
  note.className = 'ws-note';
  const kicker = document.createElement('div');
  kicker.className = 'ws-note-k';
  kicker.textContent = 'Pilot Simulator';
  const text = document.createElement('p');
  text.className = 'ws-note-t';
  text.textContent = NOTE;
  const rule = document.createElement('p');
  rule.className = 'ws-note-rule';
  rule.textContent = RULE;
  note.append(kicker, text, rule);
  ov.appendChild(note);
  document.body.appendChild(ov);

  requestAnimationFrame(() => ov.classList.add('ws-on'));
  const finish = () => {
    ov.classList.add('ws-out');
    setTimeout(() => ov.remove(), 900);
  };
  setTimeout(navigate, 550); // route swap happens under full black
  setTimeout(() => ov.classList.add('ws-stars'), 750);
  setTimeout(() => ov.classList.add('ws-note-on'), 1500);
  // impatient pilots can tap through once the note is up
  setTimeout(() => ov.addEventListener('pointerdown', finish), 1700);
  setTimeout(finish, 4800);
}

export default function WelcomeSecret({ name }: { name: string }) {
  const history = useHistory();
  const allowed = routeAllowedOnInstance('/pilot-simulator');
  const [stage, setStage] = useState(0);
  const popRef = useRef<HTMLSpanElement>(null);
  const motesRef = useRef<HTMLSpanElement>(null);
  const preloaded = useRef(false);
  const revealing = useRef(false);

  const spawnMotes = useCallback((n: number) => {
    const layer = motesRef.current;
    if (!layer) return;
    for (let i = 0; i < n; i++) {
      const m = document.createElement('span');
      m.className = 'ws-mote';
      const color = MOTE_COLORS[Math.floor(Math.random() * MOTE_COLORS.length)];
      m.style.background = color;
      m.style.boxShadow = '0 0 8px ' + color;
      m.style.left = Math.random() * 100 + '%';
      m.style.top = Math.random() * 30 + '%';
      m.style.setProperty('--dx', (Math.random() * 44 - 22).toFixed(0) + 'px');
      m.style.animationDelay = (Math.random() * 0.12).toFixed(2) + 's';
      layer.appendChild(m);
      setTimeout(() => m.remove(), 1400);
    }
  }, []);

  const onProgress = useCallback(
    (n: number) => {
      if (revealing.current) return;
      setStage(n);
      if (!n) return;
      // warm the lazy route chunk while the player is still tapping: by the
      // time the stars are out the game is in memory and the fade never stalls
      if (n >= 3 && !preloaded.current) {
        preloaded.current = true;
        // a failed preload is not an error: React.lazy retries at navigation
        import('./PilotSimulator').catch(() => {});
      }
      const pop = popRef.current;
      if (pop) {
        pop.classList.remove('ws-popping');
        void pop.offsetWidth; // restart the animation
        pop.classList.add('ws-popping');
      }
      spawnMotes(MOTES_PER_STAGE[Math.min(n, 6)]);
    },
    [spawnMotes]
  );

  const onUnlock = useCallback(() => {
    if (revealing.current) return;
    revealing.current = true;
    setStage(6); // hold peak glow through the bloom
    spawnMotes(16);
    runReveal(() => history.push('/pilot-simulator'));
  }, [history, spawnMotes]);

  const tap = useTapSecret(onUnlock, { count: TAPS, gapMs: GAP_MS, onProgress });

  if (!allowed) {
    // Swarm gets a plain greeting: no handlers, no hint that a door exists
    return <span className="username text-3xl font-bold text-primary">{name}</span>;
  }

  return (
    <>
      <span
        className={'ws-hit' + (stage ? ' ws-s' + Math.min(stage, 6) : '')}
        onPointerDown={(e) => {
          e.preventDefault();
          tap();
        }}
      >
        <span className="ws-pop" ref={popRef}>
          <span className="username text-3xl font-bold text-primary">{name}</span>
        </span>
        <span className="ws-motes" ref={motesRef} aria-hidden="true" />
      </span>
      {stage >= 5 && <span className="ws-vignette" aria-hidden="true" />}
    </>
  );
}
